// the app-update signal, kept separate from the code that registers the service worker.
//
// registration has to import `virtual:pwa-register`, a module vite-plugin-pwa synthesises
// during a build and which therefore cannot be resolved under vitest. keeping that import
// in an entry-only module (registerPwa.ts) leaves everything components touch — this file —
// plainly importable and testable.

type Listener = () => void

const listeners = new Set<Listener>()

// set by the registration once a worker is waiting; reloads onto the new build
let applyUpdate: (() => void) | null = null

// null until registration resolves either way, so callers can tell "not yet known" from
// "no worker" and avoid running the fallback poller in the gap
let serviceWorkerActive: boolean | null = null
const activationListeners = new Set<Listener>()

// both signals are latched rather than edge-only. registration resolves independently of
// react's render/effect cycle, so a listener that subscribes a tick late would otherwise
// miss the notification for good — leaving the update prompt lost for the session, or the
// fallback poller switched off forever because the activation result never arrived.
let updateAvailable = false

export function onUpdateAvailable(listener: Listener): () => void {
  listeners.add(listener)
  if (updateAvailable) listener()
  return () => listeners.delete(listener)
}

export function notifyUpdateAvailable(): void {
  updateAvailable = true
  listeners.forEach((listener) => listener())
}

export function setUpdateApplier(apply: () => void): void {
  applyUpdate = apply
}

// applies a pending update, falling back to a plain reload where no worker is involved
export function reloadForUpdate(): void {
  if (applyUpdate) {
    applyUpdate()
    return
  }
  window.location.reload()
}

export function setServiceWorkerActive(active: boolean): void {
  serviceWorkerActive = active
  activationListeners.forEach((listener) => listener())
}

export function getServiceWorkerActive(): boolean | null {
  return serviceWorkerActive
}

export function onServiceWorkerActivation(listener: Listener): () => void {
  activationListeners.add(listener)
  // replay an outcome that landed before this listener existed
  if (serviceWorkerActive !== null) listener()
  return () => activationListeners.delete(listener)
}

// asks the worker to flush the expense outbox once connectivity returns, even with the app
// closed. chromium-only; elsewhere the queue drains when the app is next opened.
export async function requestOutboxSync(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  try {
    // `serviceWorker.ready` never settles when nothing is registered (dev server, or a
    // browser that refused the worker), so ask for the registration instead
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration) return
    await (
      registration as ServiceWorkerRegistration & {
        sync?: { register: (tag: string) => Promise<void> }
      }
    ).sync?.register('fp3-expense-outbox')
  } catch {
    // background sync unavailable or blocked — the in-page triggers still drain the queue
  }
}
